import { MagnifyingGlassIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

import { useGetAmenities } from '@/api/reservations/get-amenities'
import { TableGroup } from '@/components/layout/table-group'
import { TextInput } from '@/components/ui/text-input'
import { useFilters, useSearchDraft, useSearchParams } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import {
  AMENITIES_TABLE_ID,
  amenitiesTableFiltersSchema,
  amenitiesTableInitialFilters,
  getAmenitiesTableColumns,
} from './amenities.constants'
import { matchesSearch } from './amenities.utils'

export const AmenitiesTab = () => {
  const { t } = useTranslation(['reservations'])
  const navigate = useNavigate()
  const activeBuilding = useSelector(selectActiveBuilding)
  const buildingId = activeBuilding?.id

  const searchParams = useSearchParams({
    initialState: amenitiesTableInitialFilters,
    schema: amenitiesTableFiltersSchema,
  })

  const tableFilters = useFilters({
    controlledState: searchParams.parsedQuery,
    initialState: amenitiesTableInitialFilters,
    onFiltersChange: searchParams.writeParams,
  })

  const { data: amenities = [], isFetching } = useGetAmenities(buildingId)

  const [searchDraft, handleSearchChange] = useSearchDraft(
    tableFilters.getFilter('search'),
    (value) => tableFilters.setFilter('search', value),
  )

  const search = tableFilters.getFilter('search')?.trim().toLowerCase()

  // Note: backend already returns amenities newest-first (createdAt DESC), matching the
  // ticket's required row order - no client-side re-sort needed.
  const filteredAmenities = useMemo(() => {
    if (!search) return amenities
    return amenities.filter((amenity) => matchesSearch(amenity, search))
  }, [amenities, search])

  const skip = tableFilters.getFilter('skip') ?? 0
  const limit = tableFilters.getFilter('limit') ?? amenitiesTableInitialFilters.limit ?? 25

  const pagedAmenities = useMemo(
    () => filteredAmenities.slice(skip, skip + limit),
    [filteredAmenities, skip, limit],
  )

  const columns = useMemo(() => getAmenitiesTableColumns(t), [t])

  return (
    <TableGroup
      id={AMENITIES_TABLE_ID}
      columns={columns}
      data={pagedAmenities}
      emptyNoFilters={{
        description: t('reservations:amenities.empty.noFilters.description'),
        title: t('reservations:amenities.empty.noFilters.title'),
      }}
      emptyWithFilters={{
        description: t('reservations:amenities.empty.withFilters.description'),
        title: t('reservations:amenities.empty.withFilters.title'),
      }}
      enableColumnVisibility={false}
      getRowKey={(amenity) => amenity.id}
      loading={isFetching}
      navigation={{
        search: (
          <TextInput
            clearable
            icon={MagnifyingGlassIcon}
            name="search"
            placeholder={t('reservations:amenities.search.placeholder')}
            size="small"
            value={searchDraft}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        ),
      }}
      pagination={{
        paginationFilters: tableFilters,
        total: filteredAmenities.length,
      }}
      rowActions={[
        {
          icon: PencilSimpleIcon,
          label: t('reservations:amenities.actions.edit'),
          onClick: (amenity) =>
            navigate(`/dashboard/building-dashboard/reservations/amenities/edit/${amenity.id}`),
          primary: true,
        },
      ]}
      tableFilters={tableFilters}
    />
  )
}
